// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — useProcessGroups hook
// Fetches Level 1 process map data grouped by tipologia
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import type { TipologiaGroup } from '../types/process-map.types';
import type { ProcessGroup } from '../../flow/types/flowchart.types';
import { TIPOLOGIA_CONFIG } from '../../../../../lib/design';
import { useI18n } from '../../../../../i18n';
import { request } from '../../../../shared/services/api';

export function useProcessGroups() {
  const { lang } = useI18n();
  const [tipologias, setTipologias] = useState<TipologiaGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError(null);

        type ProcesoDTO = {
          id: string;
          codigo: string;
          nombre: string;
          tipologia: 'PE' | 'PO' | 'PS';
          grupoNumero: number;
          grupoNombre: string;
          fase?: string;
          responsable?: { titulo: string };
          _count?: { documentos: number };
        };
        const json = await request<{ data?: ProcesoDTO[]; datos?: ProcesoDTO[] }>('/procesos');
        const procesos: ProcesoDTO[] = json.data ?? json.datos ?? [];

        // Group by tipologia, then by grupoNumero
        const byTipologia = new Map<string, Map<number, ProcessGroup>>();

        for (const p of procesos) {
          let groupMap = byTipologia.get(p.tipologia);
          if (!groupMap) {
            groupMap = new Map();
            byTipologia.set(p.tipologia, groupMap);
          }
          let group = groupMap.get(p.grupoNumero);
          if (!group) {
            group = {
              grupoNumero: p.grupoNumero,
              grupoNombre: p.grupoNombre,
              tipologia: p.tipologia,
              procesos: [],
            };
            groupMap.set(p.grupoNumero, group);
          }
          group.procesos.push({
            id: p.id,
            codigo: p.codigo,
            nombre: p.nombre,
            fase: p.fase,
            responsable: p.responsable,
            documentCount: p._count?.documentos ?? 0,
          });
        }

        // Order: PE, PO, PS
        const order: Array<'PE' | 'PO' | 'PS'> = ['PE', 'PO', 'PS'];
        const result: TipologiaGroup[] = order
          .filter((t) => byTipologia.has(t))
          .map((t) => {
            const cfg = TIPOLOGIA_CONFIG[t];
            const groups = Array.from((byTipologia.get(t) ?? new Map()).values()).sort(
              (a, b) => a.grupoNumero - b.grupoNumero,
            );
            return {
              tipologia: t,
              label: cfg.label,
              color: cfg.color,
              groups,
            };
          });

        if (!cancelled) setTipologias(result);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [lang]);

  return { tipologias, loading, error };
}
